import { useCallback, useEffect, useRef, useState } from "react";
import {
  Play, Pause, Rewind, FastForward, ChevronLeft, ChevronRight, ChevronDown, Gauge, Check, ListVideo, CloudOff, MessageSquarePlus,
} from "lucide-react";
import { audioUrl, SPEED_KEY, AUTOPLAY_KEY } from "@/lib/listenApi";
import { ListenShell, fmtTime } from "./listenShared";
import { FeedbackSheet } from "./ListenFeedback";
import { SILENT_WAV } from "@/lib/silentAudio";
import * as offline from "@/lib/offline";

const SPEEDS = [0.75, 1.0, 1.25, 1.5, 1.75, 2.0];

export function ListenPlayer({ items, releaseId, startIndex, startPosition, token, meta, preview, cover, coverArtworkUrl, onExit }) {
  const audioRef = useRef(null);
  const [index, setIndex] = useState(startIndex);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [speed, setSpeed] = useState(() => parseFloat(localStorage.getItem(SPEED_KEY)) || 1.0);
  const [autoplay, setAutoplay] = useState(() => localStorage.getItem(AUTOPLAY_KEY) !== "0");
  const [done, setDone] = useState(false);
  const [endState, setEndState] = useState(null); // section | book | blocked
  const [unavailable, setUnavailable] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [notePos, setNotePos] = useState(0);
  const seekRef = useRef(startPosition || 0);
  const blobUrlRef = useRef(null);
  const anchorRef = useRef(null);
  const doneRef = useRef(false);
  const speedRef = useRef(speed);
  const autoplayRef = useRef(autoplay);
  const item = items[index];
  const next = items[index + 1];
  const nextPlayIdx = (() => { for (let i = index + 1; i < items.length; i += 1) if (items[i]?.has_audio) return i; return -1; })();
  const nextPlay = nextPlayIdx >= 0 ? items[nextPlayIdx] : null;
  useEffect(() => { speedRef.current = speed; }, [speed]);
  useEffect(() => { doneRef.current = done; }, [done]);
  useEffect(() => { autoplayRef.current = autoplay; }, [autoplay]);

  // Silent "anchor" element: an always-buffered clip we play alongside the streamed
  // narration. On iOS the audio session is suspended while the phone is locked, so a
  // stalled network <audio> can't reacquire it and lock-screen Play is ignored (Pause
  // still works). Kicking this instantly-available clip in the same gesture reactivates
  // the session so the real audio can resume.
  useEffect(() => {
    const anc = new Audio(SILENT_WAV);
    anc.loop = true; anc.volume = 0; anc.preload = "auto";
    anchorRef.current = anc;
    return () => { try { anc.pause(); } catch { /* ignore */ } anchorRef.current = null; };
  }, []);

  const resumePlayback = useCallback(async () => {
    const a = audioRef.current;
    if (!a) return;
    try { const anc = anchorRef.current; if (anc) { anc.currentTime = 0; await anc.play().catch(() => {}); } } catch { /* ignore */ }
    try {
      await a.play();
    } catch {
      // iOS may have released the media element while backgrounded — reload and retry once.
      try { const pos = a.currentTime; a.load(); a.currentTime = pos; await a.play(); } catch { /* give up quietly */ }
    }
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing";
  }, []);

  const pausePlayback = useCallback(() => {
    try { audioRef.current?.pause(); } catch { /* ignore */ }
    try { anchorRef.current?.pause(); } catch { /* ignore */ }
    if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused";
  }, []);

  const toggleAutoplay = () => {
    const v = !autoplay;
    setAutoplay(v);
    localStorage.setItem(AUTOPLAY_KEY, v ? "1" : "0");
  };

  const save = useCallback(
    (completed = false) => {
      const a = audioRef.current;
      const it = items[index];
      if (!a || !it || preview) return;
      offline.recordProgress(releaseId, it, a.currentTime, isFinite(a.duration) ? a.duration : it.duration, completed)
        .then(() => { if (navigator.onLine) offline.flushOutbox(token); })
        .catch(() => {});
    },
    [items, index, releaseId, token, preview]
  );

  const skip = useCallback((d) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = Math.max(0, Math.min((a.duration || 0), a.currentTime + d));
  }, []);

  const go = useCallback((i, skipSave = false) => {
    if (i < 0 || i >= items.length) return;
    if (!skipSave && !doneRef.current) save(false);
    seekRef.current = 0;
    setEndState(null);
    setDone(false);
    setIndex(i);
    setPlaying(true);
  }, [items, save]);

  // Load a new item — prefer a downloaded offline blob, else stream, else show
  // an offline-unavailable notice (never a network error).
  useEffect(() => {
    let cancelled = false;
    const a = audioRef.current;
    const it = items[index];
    if (!a || !it) return;
    setDone(false); setUnavailable(false); setT(0); setDur(it.duration || 0);
    if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
    (async () => {
      const blobUrl = await offline.getAudioBlobUrl(releaseId, it);
      if (cancelled) { if (blobUrl) URL.revokeObjectURL(blobUrl); return; }
      if (blobUrl) {
        blobUrlRef.current = blobUrl;
        a.src = blobUrl;
      } else if (navigator.onLine) {
        a.src = audioUrl(it.item_id, token);
      } else {
        setUnavailable(true);
        return;
      }
      a.playbackRate = speedRef.current;
      a.load();
    })();
    return () => { cancelled = true; };
  }, [index, items, releaseId, token]);

  useEffect(() => () => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); }, []);

  // Periodic + lifecycle saves.
  useEffect(() => {
    const id = setInterval(() => { if (!audioRef.current?.paused) save(false); }, 12000);
    const onHide = () => save(false, true);
    const onVis = () => { if (document.visibilityState === "hidden") save(false, true); };
    window.addEventListener("pagehide", onHide);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      window.removeEventListener("pagehide", onHide);
      document.removeEventListener("visibilitychange", onVis);
      save(false);
    };
  }, [save]);

  // Media Session (lock-screen) integration.
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    const it = items[index];
    if (!it) return;
    try {
      const MM = window.MediaMetadata;
      if (MM) {
        navigator.mediaSession.metadata = new MM({
          title: it.title,
          artist: meta.author || "House Diamond",
          album: meta.book_title || "House Diamond",
          artwork: coverArtworkUrl
            ? [{ src: coverArtworkUrl, sizes: "1024x1024", type: "image/jpeg" }]
            : [
              { src: "/listen-icon-192.png", sizes: "192x192", type: "image/png" },
              { src: "/listen-icon-512.png", sizes: "512x512", type: "image/png" },
            ],
        });
      }
    } catch { /* ignore */ }
    const ms = navigator.mediaSession;
    const set = (act, fn) => { try { ms.setActionHandler(act, fn); } catch { /* unsupported */ } };
    set("play", () => resumePlayback());
    set("pause", () => pausePlayback());
    set("seekbackward", () => skip(-15));
    set("seekforward", () => skip(15));
    set("previoustrack", index > 0 ? () => go(index - 1) : null);
    set("nexttrack", items[index + 1] ? () => go(index + 1) : null);
    set("seekto", (d) => { if (audioRef.current && d.seekTime != null) audioRef.current.currentTime = d.seekTime; });
  }, [index, items, meta.author, meta.book_title, coverArtworkUrl, go, skip, resumePlayback, pausePlayback]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) resumePlayback(); else pausePlayback();
  };
  const cycleSpeed = () => {
    const nextSpeed = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length] || 1.0;
    setSpeed(nextSpeed);
    localStorage.setItem(SPEED_KEY, String(nextSpeed));
    if (audioRef.current) audioRef.current.playbackRate = nextSpeed;
  };

  const openNote = () => {
    const a = audioRef.current;
    if (a && !a.paused) pausePlayback();
    setNotePos(a ? a.currentTime : (t || 0));
    setFeedbackOpen(true);
  };

  const onLoaded = () => {
    const a = audioRef.current;
    if (!a) return;
    setDur(a.duration || item?.duration || 0);
    a.playbackRate = speed;
    if (seekRef.current > 0 && seekRef.current < (a.duration || Infinity)) {
      a.currentTime = seekRef.current;
    }
    if (playing) resumePlayback();
  };
  // Continuous playback: mark complete, then auto-advance to the next available
  // item in reading order (streaming online, or a downloaded blob offline).
  const onEnded = async () => {
    setPlaying(false);
    try { anchorRef.current?.pause(); } catch { /* ignore */ }
    save(true); // completion is monotonic
    if (nextPlayIdx < 0) { setEndState("book"); setDone(true); return; }
    if (!autoplayRef.current) { setEndState("section"); setDone(true); return; }
    if (navigator.onLine) { go(nextPlayIdx, true); return; }
    const has = await offline.isDownloaded(releaseId, items[nextPlayIdx]);
    if (has) { go(nextPlayIdx, true); return; }
    setEndState("blocked"); setDone(true);
  };
  const updatePos = () => {
    const a = audioRef.current;
    if (a && "mediaSession" in navigator && isFinite(a.duration)) {
      try {
        navigator.mediaSession.setPositionState({ duration: a.duration, position: a.currentTime, playbackRate: a.playbackRate });
      } catch { /* ignore */ }
    }
  };

  return (
    <ListenShell>
      <audio
        ref={audioRef}
        data-testid="listen-audio"
        preload="metadata"
        onLoadedMetadata={onLoaded}
        onTimeUpdate={() => { setT(audioRef.current?.currentTime || 0); updatePos(); }}
        onPlay={() => { setPlaying(true); try { anchorRef.current?.play().catch(() => {}); } catch { /* ignore */ } if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "playing"; }}
        onPause={() => { setPlaying(false); const a = audioRef.current; try { anchorRef.current?.pause(); } catch { /* ignore */ } if (a && !done && a.currentTime < (a.duration || 0) - 0.4) save(false); if ("mediaSession" in navigator) navigator.mediaSession.playbackState = "paused"; }}
        onEnded={onEnded}
      />

      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-6 pb-10 pt-6">
        <div className="flex items-center justify-between">
          <button data-testid="listen-player-back" onClick={() => onExit(true)} className="flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200">
            <ChevronDown className="h-5 w-5" /> Library
          </button>
          <button
            data-testid="listen-autoplay-toggle"
            aria-pressed={autoplay}
            onClick={toggleAutoplay}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${autoplay ? "border-teal-500/40 bg-teal-500/10 text-teal-200" : "border-white/10 text-slate-400 hover:text-slate-200"}`}
          >
            <ListVideo className="h-3.5 w-3.5" /> Autoplay {autoplay ? "On" : "Off"}
          </button>
        </div>
        {preview && (
          <div className="mt-3 flex justify-center">
            <span data-testid="listen-preview-badge" className="rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest text-amber-300">
              Preview Mode
            </span>
          </div>
        )}

        <div className="flex flex-1 flex-col items-center justify-center">
          {cover ? (
            <div className="w-full max-w-[440px] overflow-hidden rounded-[2rem] border border-white/5 shadow-2xl shadow-black/40">
              <img
                data-testid="listen-player-cover"
                src={cover}
                alt={(meta.book_title || "Book") + " cover art"}
                className={`aspect-square w-full object-cover transition-transform duration-500 ${playing ? "scale-100" : "scale-[0.99]"}`}
              />
            </div>
          ) : (
            <div className="grid h-56 w-56 place-items-center overflow-hidden rounded-[2rem] border border-white/5 bg-gradient-to-br from-teal-500/[0.10] to-white/[0.02] shadow-2xl shadow-black/40 sm:h-64 sm:w-64">
              <div
                className={`grid h-40 w-40 place-items-center rounded-2xl bg-[#0b0f15] transition-transform ${playing ? "scale-100" : "scale-95"}`}
                style={{ boxShadow: "inset 0 0 40px rgba(45,212,191,0.10)" }}
              >
                <span className="font-serif text-6xl text-teal-300/30">◆</span>
              </div>
            </div>
          )}
          <div className="mt-7 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-teal-300/70">{meta.book_title || "House Diamond"}</div>
            <h2 data-testid="listen-now-title" className="mt-2 font-serif text-2xl text-slate-50">{item?.title}</h2>
          </div>
        </div>

        <div className="mt-6">
          <input
            data-testid="listen-scrubber"
            type="range"
            min={0}
            max={Math.max(dur, 0.1)}
            step={0.5}
            value={Math.min(t, dur || 0)}
            onChange={(e) => { if (audioRef.current) { audioRef.current.currentTime = parseFloat(e.target.value); setT(parseFloat(e.target.value)); } }}
            className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-white/10 accent-teal-400 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-teal-300"
          />
          <div className="mt-2 flex justify-between font-mono text-[11px] text-slate-500">
            <span data-testid="listen-current-time">{fmtTime(t)}</span>
            <span>{fmtTime(dur)}</span>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button data-testid="listen-speed" onClick={cycleSpeed} className="flex items-center gap-1 rounded-full border border-white/10 px-3 py-1.5 text-xs font-medium text-slate-300 hover:border-teal-500/40 hover:text-teal-200">
            <Gauge className="h-3.5 w-3.5" /> {speed}x
          </button>
          <div className="flex items-center gap-5">
            <button data-testid="listen-prev" onClick={() => go(index - 1)} disabled={index === 0} className="text-slate-400 disabled:opacity-25 hover:text-slate-100">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button data-testid="listen-back15" onClick={() => skip(-15)} className="relative text-slate-200 hover:text-teal-200">
              <Rewind className="h-6 w-6" fill="currentColor" />
            </button>
            <button
              data-testid="listen-playpause"
              onClick={toggle}
              className="grid h-16 w-16 place-items-center rounded-full bg-teal-400 text-slate-950 transition-transform hover:bg-teal-300 active:scale-95"
            >
              {playing ? <Pause className="h-7 w-7" fill="currentColor" /> : <Play className="ml-0.5 h-7 w-7" fill="currentColor" />}
            </button>
            <button data-testid="listen-fwd15" onClick={() => skip(15)} className="text-slate-200 hover:text-teal-200">
              <FastForward className="h-6 w-6" fill="currentColor" />
            </button>
            <button data-testid="listen-next" onClick={() => go(index + 1)} disabled={!next} className="text-slate-400 disabled:opacity-25 hover:text-slate-100">
              <ChevronRight className="h-6 w-6" />
            </button>
          </div>
          <button
            data-testid="listen-add-note"
            onClick={openNote}
            title="Leave a note at this moment"
            className="flex flex-col items-center gap-0.5 text-slate-400 hover:text-teal-200"
          >
            <MessageSquarePlus className="h-6 w-6" />
            <span className="text-[9px] uppercase tracking-wider">Note</span>
          </button>
        </div>
      </div>

      <FeedbackSheet
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        token={token}
        item={item}
        position={notePos}
        preview={preview}
      />

      {unavailable && (
        <div data-testid="listen-offline-unavailable" className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#07090d]/95 px-8 text-center backdrop-blur">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-slate-700/30"><ChevronDown className="h-8 w-8 rotate-90 text-slate-400" /></span>
          <h3 className="mt-6 font-serif text-2xl text-slate-50">This section isn&apos;t available offline</h3>
          <p className="mt-2 max-w-xs text-sm text-slate-400">Download it while you&apos;re connected, then it&apos;ll play anywhere.</p>
          <button data-testid="listen-unavailable-back" onClick={() => onExit(true)} className="mt-8 rounded-full bg-teal-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-300">Back to Library</button>
        </div>
      )}
      {done && endState === "section" && (
        <div data-testid="listen-complete" className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#07090d]/95 px-8 text-center backdrop-blur">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-teal-500/15"><Check className="h-8 w-8 text-teal-300" /></span>
          <h3 className="mt-6 font-serif text-2xl text-slate-50">Section Complete</h3>
          <p className="mt-2 text-sm text-slate-400">{item?.title}</p>
          <div className="mt-9 flex flex-col gap-3">
            {nextPlay && (
              <button data-testid="listen-continue-next" onClick={() => go(nextPlayIdx)} className="rounded-full bg-teal-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-300">
                Continue to {nextPlay.title}
              </button>
            )}
            <button data-testid="listen-complete-library" onClick={() => onExit(true)} className="text-sm text-slate-400 hover:text-slate-200">
              Back to Library
            </button>
          </div>
        </div>
      )}
      {done && endState === "book" && (
        <div data-testid="listen-book-complete" className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#07090d]/95 px-8 text-center backdrop-blur">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-teal-500/15"><Check className="h-8 w-8 text-teal-300" /></span>
          <h3 className="mt-6 font-serif text-2xl text-slate-50">Book Complete</h3>
          <p className="mt-2 max-w-xs text-sm text-slate-400">You&apos;ve reached the end of this release. Thank you for listening.</p>
          <button data-testid="listen-complete-library" onClick={() => onExit(true)} className="mt-9 rounded-full bg-teal-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-300">
            Back to Library
          </button>
        </div>
      )}
      {done && endState === "blocked" && (
        <div data-testid="listen-next-unavailable" className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-[#07090d]/95 px-8 text-center backdrop-blur">
          <span className="grid h-16 w-16 place-items-center rounded-full bg-slate-700/30"><CloudOff className="h-8 w-8 text-slate-400" /></span>
          <h3 className="mt-6 font-serif text-2xl text-slate-50">Next section isn&apos;t downloaded</h3>
          <p className="mt-2 max-w-xs text-sm text-slate-400">You&apos;re offline. Connect to the internet, or download this section from your library, to keep listening.</p>
          <div className="mt-9 flex flex-col gap-3">
            <button data-testid="listen-continue-next" onClick={() => go(nextPlayIdx)} className="rounded-full bg-teal-400 px-8 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-300">
              Try to continue
            </button>
            <button data-testid="listen-complete-library" onClick={() => onExit(true)} className="text-sm text-slate-400 hover:text-slate-200">
              Back to Library
            </button>
          </div>
        </div>
      )}
    </ListenShell>
  );
}
